/* Bootfitting page: the interactive parts of the "Werkstatt" concept.
   - <bootfitting-check>: the Passform-Check. Zones on the boot drawing, a few
     questions per appointment type, an assessment built from the section's
     blocks, and a one-line brief the customer takes into the booking.
   - <bootfitting-anatomy>: the layer tabs on the analysis sheet.
   - <bootfitting-costs>: the cost cases, panels rendered by Liquid.
   - [data-bf-mode] buttons anywhere on the page switch the check's appointment
     type; ?modus=schuh|neu|nachkontrolle does the same on load, and
     &pass=SB-JJMM-NNNN (the QR code on the Passform-Pass) fills the pass number.
   - Booking buttons ([data-bf-book]: hero, check card, visit, booking bar)
     open Meety's widget in place (assets/meety-launch.js) instead of
     scrolling to the booking area; without Meety they stay anchor links.
   - Meety's booking widget (app block in the place section) gets the check's
     answers once the customer has picked an appointment type: matching card
     preselected, empty form fields filled. A booking button for one type also
     moves the widget on from its service list to that type's calendar.
   - The booking bar at the bottom of the screen (check setting sticky_bar).
   Nothing leaves the browser until the customer confirms the booking in
   Meety. Brief and answers are kept in sessionStorage so the booking section
   can show them and the widget can be filled after a reload. */

(() => {
  'use strict';

  /* Several sections load this file; the browser executes each tag. */
  if (window.__bootfittingPage) return;
  window.__bootfittingPage = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const esc = (value) =>
    String(value ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const fill = (template, values) =>
    String(template ?? '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (values[key] ?? ''));
  /* "45 Min · 50 € · " when a setting is empty: drop the dangling separators. */
  const tidy = (text) => text.replace(/^(?:\s*·\s*)+|(?:\s*·\s*)+$/g, '').replace(/(?:\s*·\s*){2,}/g, ' · ');
  const scrollTo = (element) => {
    if (!element) return;
    element.scrollIntoView({ block: 'start', behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  };
  const ENTRY = { neu: 'new', schuh: 'own', nachkontrolle: 'follow' };
  const optionValue = (option) => (Array.isArray(option) ? option[0] : option);
  const BRIEF_KEY = 'bf-brief';
  const BOOKING_KEY = 'bf-booking';

  /* The widget of Meety's button block in the place section, e.g.
     "widgets/bootfitting". Booking buttons carry it as data-meety-widget, so
     assets/meety-launch.js opens that widget in place when they are clicked. */
  const WIDGET_PATH = '/website-widget/';
  const pageWidget = () => {
    const link = document.querySelector(`bootfitting-booking a[href*="/apps/meety-appointment-booking${WIDGET_PATH}"]`);
    const href = link ? link.getAttribute('href') : '';
    const at = href.indexOf(WIDGET_PATH);
    return at === -1 ? '' : decodeURIComponent(href.slice(at + WIDGET_PATH.length).split(/[?#]/)[0]).trim();
  };
  const bookAttributes = (mode) => {
    const widget = pageWidget();
    return `data-bf-book${mode ? ` data-bf-book-mode="${esc(mode)}"` : ''}${widget ? ` data-meety-widget="${esc(widget)}"` : ''}`;
  };

  /* Meety's custom questions carry a German and an English version (Meety
     admin, per question), shown by storefront language. The script finds a
     question by either label and a radio option by either text; it stores the
     German value. The service cards show the product title, which on /en/ is
     the product's English translation, so a card is found by either name
     (lower case, part of the title). Renaming a question or option in Meety
     only stops that one field from being filled; docs/bootfitting-page.md
     lists them. */
  const MEETY = {
    services: {
      own: ['werkstatt-termin', 'workshop appointment'],
      new: ['beratungs-termin', 'consultation'],
      follow: ['nachkontrolle', 'follow-up check'],
    },
    concern: ['Dein Anliegen', 'Your request'],
    model: ['Marke und Modell', 'Make and model'],
    bought: {
      label: ['Gekauft bei', 'Bought at'],
      options: [
        ['Bei Sport Schindele', 'At Sport Schindele'],
        ['Woanders', 'Elsewhere'],
      ],
    },
    /* Check: Piste, Skitour, Freeride, Piste und Tour, Wander- oder Bergschuh. */
    use: {
      label: ['Einsatz', 'Use'],
      options: [['Piste', 'Piste'], ['Skitour', 'Ski touring'], ['Freeride', 'Freeride'], ['Gemischt', 'Mixed'], ['']],
    },
  };

  /* Fixed positions of the eight zones on the boot drawing (viewBox 480 x 470,
     offset 40/70 of the symbol). Content comes from the section's blocks. */
  const ZONE_GEOMETRY = {
    wade: { x: 155, y: 250, pos: 'l' },
    schienbein: { x: 323, y: 250, pos: 'r' },
    rist: { x: 350, y: 371, pos: 'r' },
    knoechel: { x: 200, y: 389, pos: 'r' },
    ferse: { x: 101, y: 452, pos: 'l' },
    sohle: { x: 270, y: 487, pos: 'b' },
    ballen: { x: 372, y: 455, pos: 't' },
    zehen: { x: 450, y: 464, pos: 'b' },
  };
  const SHELL_ZONES = ['ballen', 'zehen', 'knoechel', 'wade'];

  const storeBrief = (text) => {
    try {
      if (text) sessionStorage.setItem(BRIEF_KEY, text);
      else sessionStorage.removeItem(BRIEF_KEY);
    } catch (error) {
      /* storage blocked: the brief is still on screen */
    }
    document.dispatchEvent(new CustomEvent('bf:brief', { detail: { text } }));
  };

  /* The check's answers for the booking form, kept in memory too in case
     storage is blocked. */
  let bookingAnswers = null;
  const storeBooking = (answers) => {
    bookingAnswers = answers;
    try {
      sessionStorage.setItem(BOOKING_KEY, JSON.stringify(answers));
    } catch (error) {
      /* storage blocked: the in-memory copy still fills the widget */
    }
  };
  const readBooking = () => {
    if (bookingAnswers) return bookingAnswers;
    try {
      return JSON.parse(sessionStorage.getItem(BOOKING_KEY) || 'null');
    } catch (error) {
      return null;
    }
  };

  /* Keyboard access for a row of tabs, horizontal or vertical. */
  const wireTabs = (row, onSelect) => {
    const tabs = [...row.querySelectorAll('[role="tab"]')];
    row.addEventListener('keydown', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (!tab) return;
      const vertical = getComputedStyle(row).flexDirection === 'column';
      const previous = vertical ? 'ArrowUp' : 'ArrowLeft';
      const next = vertical ? 'ArrowDown' : 'ArrowRight';
      let index = tabs.indexOf(tab);
      if (event.key === previous) index = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === next) index = (index + 1) % tabs.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else return;
      event.preventDefault();
      tabs[index].focus({ preventScroll: true });
      onSelect(tabs[index]);
    });
    tabs.forEach((tab) => tab.addEventListener('click', () => onSelect(tab)));
    const updateOrientation = () =>
      row.setAttribute('aria-orientation', getComputedStyle(row).flexDirection === 'column' ? 'vertical' : 'horizontal');
    window.addEventListener('resize', updateOrientation, { passive: true });
    updateOrientation();
    return tabs;
  };

  const selectTab = (tabs, attribute, value) => {
    tabs.forEach((tab) => {
      const selected = tab.dataset[attribute] === value;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
  };

  /* ---------------------------------------------------------------- check */
  class BootfittingCheck extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const raw = this.querySelector('script[data-bf-check]');
      this.data = raw ? JSON.parse(raw.textContent) : null;
      if (!this.data) return;
      this.strings = this.data.strings;
      this.state = {
        mode: 'own',
        /* Picked by the customer (tab, situation card, deep link, an answer,
           a booking button), not just the default tab. Only then does the
           widget get an appointment type preselected. */
        chosen: false,
        zones: new Set(),
        general: new Set(),
        when: '',
        use: '',
        model: '',
        bought: '',
        features: new Set(),
        history: '',
        passNo: '',
        skidays: '',
      };
      /* Not `this.title`: that is HTMLElement's own string attribute. */
      this.titleEl = this.querySelector('[data-bf-title]');
      this.subEl = this.querySelector('[data-bf-sub]');
      this.boot = this.querySelector('[data-bf-boot]');
      this.zonesEl = this.querySelector('[data-bf-zones]');
      this.hint = this.querySelector('[data-bf-hint]');
      this.jump = this.querySelector('[data-bf-jump]');
      this.form = this.querySelector('[data-bf-form]');
      this.result = this.querySelector('[data-bf-result]');
      this.modeTabs = wireTabs(this.querySelector('[data-bf-modes]'), (tab) => this.setMode(tab.dataset.mode, false));
      this.renderZones();
      this.form.addEventListener('input', (event) => this.onInput(event));
      this.zonesEl.addEventListener('click', (event) => this.onZone(event));
      this.result.addEventListener('click', (event) => this.onResultClick(event));
      this.jump.addEventListener('click', () => scrollTo(this.result));
      if (this.data.stickyBar) this.bar = new BookingBar(this);
      this.setMode('own', false, false);
    }

    renderZones() {
      this.zonesEl.innerHTML = this.data.zones
        .map((zone) => {
          const geometry = ZONE_GEOMETRY[zone.key];
          if (!geometry) return '';
          const left = (((geometry.x - 40) / 480) * 100).toFixed(2);
          const top = (((geometry.y - 70) / 470) * 100).toFixed(2);
          return `<button type="button" class="bf-zone bf-zone--${geometry.pos}" data-zone="${esc(zone.key)}" aria-pressed="false" style="left:${left}%;top:${top}%"><i></i><span>${esc(zone.label)}</span></button>`;
        })
        .join('');
    }

    zone(key) {
      return this.data.zones.find((zone) => zone.key === key);
    }

    general(key) {
      return this.data.general.find((item) => item.key === key);
    }

    feature(key) {
      return this.data.features.find((item) => item.key === key);
    }

    onZone(event) {
      const button = event.target.closest('.bf-zone');
      if (!button) return;
      const key = button.dataset.zone;
      if (this.state.zones.has(key)) this.state.zones.delete(key);
      else this.state.zones.add(key);
      button.setAttribute('aria-pressed', String(this.state.zones.has(key)));
      this.state.chosen = true;
      this.renderResult();
    }

    onInput(event) {
      const field = event.target;
      const name = field.name;
      if (!name) return;
      this.state.chosen = true;
      if (field.type === 'checkbox') {
        if (field.checked) this.state[name].add(field.value);
        else this.state[name].delete(field.value);
      } else {
        this.state[name] = field.value;
      }
      this.renderResult();
    }

    onResultClick(event) {
      const copy = event.target.closest('[data-bf-copy]');
      if (!copy) return;
      const text = this.brief();
      const done = () => {
        copy.textContent = this.strings.copied;
        setTimeout(() => {
          copy.textContent = this.strings.copy;
        }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, done);
      } else {
        done();
      }
    }

    chips(name, options, type, selected) {
      return `<div class="bf-chips">${options
        .map((option) => {
          const [value, label] = Array.isArray(option) ? option : [option, option];
          const checked = type === 'radio' ? selected === value : selected.has(value);
          return `<label><input type="${type}" name="${name}" value="${esc(value)}" ${checked ? 'checked' : ''}><span>${esc(label)}</span></label>`;
        })
        .join('')}</div>`;
    }

    renderForm() {
      const s = this.strings;
      const state = this.state;
      const mode = state.mode;
      const multi = `<small>(${esc(s.multi)})</small>`;
      let html = '';
      if (mode === 'own') {
        html = `<fieldset class="bf-q"><legend>${esc(s.legend_general)} ${multi}</legend>${this.chips('general', this.data.general.map((g) => [g.key, g.label]), 'checkbox', state.general)}</fieldset>
          <fieldset class="bf-q"><legend>${esc(s.legend_when)}</legend>${this.chips('when', s.when_options, 'radio', state.when)}</fieldset>
          <fieldset class="bf-q"><legend>${esc(s.legend_use_own)}</legend>${this.chips('use', s.use_options, 'radio', state.use)}</fieldset>
          <div class="bf-q"><label for="bf-model-${this.id || 'x'}">${esc(s.label_model)} <small>${esc(s.model_hint)}</small></label><input class="bf-field" id="bf-model-${this.id || 'x'}" name="model" value="${esc(state.model)}" placeholder="${esc(s.model_placeholder)}" autocomplete="off"><small class="bf-q__why">${esc(s.model_why)}</small></div>
          <fieldset class="bf-q"><legend>${esc(s.legend_bought)}</legend>${this.chips('bought', s.bought_options, 'radio', state.bought)}</fieldset>`;
      } else if (mode === 'new') {
        html = `<fieldset class="bf-q"><legend>${esc(s.legend_use_new)}</legend>${this.chips('use', s.use_options, 'radio', state.use)}</fieldset>
          <fieldset class="bf-q"><legend>${esc(s.legend_features)} ${multi}</legend>${this.chips('features', this.data.features.map((f) => [f.key, f.label]), 'checkbox', state.features)}</fieldset>
          <fieldset class="bf-q"><legend>${esc(s.legend_history)}</legend>${this.chips('history', s.history_options, 'radio', state.history)}</fieldset>`;
      } else {
        html = `<div class="bf-q"><label for="bf-pass-${this.id || 'x'}">${esc(s.label_pass)}</label><input class="bf-field" id="bf-pass-${this.id || 'x'}" name="passNo" value="${esc(state.passNo)}" placeholder="${esc(s.pass_placeholder)}" autocomplete="off"></div>
          <fieldset class="bf-q"><legend>${esc(s.legend_skidays)}</legend>${this.chips('skidays', s.skidays_options, 'radio', state.skidays)}</fieldset>`;
      }
      this.form.innerHTML = html;
      const off = mode === 'new';
      this.boot.classList.toggle('is-off', off);
      this.zonesEl.inert = off;
      this.hint.textContent = off ? s.hint_new : mode === 'follow' ? s.hint_follow : s.hint_default;
      const copy = this.data.copy[mode];
      if (this.titleEl && copy.title) this.titleEl.textContent = copy.title;
      if (this.subEl && copy.sub) this.subEl.textContent = copy.sub;
    }

    setMode(mode, scroll, chosen = true) {
      if (!this.data.services[mode]) mode = 'own';
      this.state.mode = mode;
      if (chosen) this.state.chosen = true;
      selectTab(this.modeTabs, 'mode', mode);
      this.renderForm();
      this.renderResult();
      if (scroll) scrollTo(this);
    }

    /* A booking button for one appointment type was clicked. */
    choose(mode) {
      if (mode !== this.state.mode) {
        this.setMode(mode, false);
        return;
      }
      if (this.state.chosen) return;
      this.state.chosen = true;
      storeBooking(this.bookingAnswers());
      this.renderBar();
    }

    /* Whether the customer has told the check anything yet. */
    answered() {
      const state = this.state;
      if (state.mode === 'new') return Boolean(state.use || state.features.size || state.history);
      if (state.zones.size || state.general.size) return true;
      if (state.mode === 'own') return Boolean(state.when || state.use || state.model.trim() || state.bought);
      return Boolean(state.passNo.trim() || state.skidays);
    }

    brief() {
      const state = this.state;
      const mode = state.mode;
      const service = this.data.services[mode];
      const parts = [service.short];
      if (mode !== 'new') {
        const picks = [...state.zones].map((key) => this.zone(key)?.label).concat([...state.general].map((key) => this.general(key)?.label)).filter(Boolean);
        if (picks.length) parts.push(picks.join(', '));
      }
      if (mode === 'own') {
        ['when', 'use', 'model', 'bought'].forEach((key) => {
          if (state[key]) parts.push(state[key]);
        });
      }
      if (mode === 'new') {
        if (state.use) parts.push(state.use);
        const features = [...state.features].filter((key) => key !== 'weiss').map((key) => this.feature(key)?.label).filter(Boolean);
        if (features.length) parts.push(features.join(', '));
        if (state.history) parts.push(state.history);
      }
      if (mode === 'follow') {
        if (state.passNo) parts.push(`${this.strings.pass_prefix} ${state.passNo}`);
        if (state.skidays) parts.push(state.skidays);
      }
      return parts.join(' · ');
    }

    /* The answers in the shape of Meety's form: the concern text for "Dein
       Anliegen" without what has its own question there, plus the mapped
       radio values. */
    bookingAnswers() {
      const s = this.strings;
      const state = this.state;
      const mode = state.mode;
      const position = (options, value) => (value ? options.map(optionValue).indexOf(value) : -1);
      const parts = [];
      if (mode !== 'new') {
        const picks = [...state.zones].map((key) => this.zone(key)?.label).concat([...state.general].map((key) => this.general(key)?.label)).filter(Boolean);
        if (picks.length) parts.push(picks.join(', '));
      }
      let use = '';
      if (mode === 'own') {
        if (state.when) parts.push(state.when);
        if (state.use) parts.push(state.use);
      }
      if (mode === 'new') {
        use = optionValue(MEETY.use.options[position(s.use_options, state.use)] || '');
        if (state.use && !use) parts.push(state.use);
        const features = [...state.features].filter((key) => key !== 'weiss').map((key) => this.feature(key)?.label).filter(Boolean);
        if (features.length) parts.push(features.join(', '));
        if (state.history) parts.push(state.history);
      }
      if (mode === 'follow') {
        if (state.passNo) parts.push(`${s.pass_prefix} ${state.passNo}`);
        if (state.skidays) parts.push(state.skidays);
      }
      return {
        mode,
        chosen: state.chosen,
        concern: parts.join(' · '),
        model: mode === 'own' ? state.model.trim() : '',
        bought: mode === 'own' ? optionValue(MEETY.bought.options[position(s.bought_options, state.bought)] || '') : '',
        use,
      };
    }

    finding(item) {
      const s = this.strings;
      const can = (item.can || []).map((line) => `<li>${esc(line)}</li>`).join('');
      const layers = (item.layers || []).map((layer) => `<span class="bf-badge">${esc(layer)}</span>`).join('');
      return `<div class="bf-finding"><h4>${esc(item.title)}</h4><p><strong>${esc(s.look_label)}</strong> ${esc(item.look)}</p>${can ? `<ul>${can}</ul>` : ''}${item.limit ? `<p class="bf-limit"><strong>${esc(s.limit_label)}</strong> ${esc(item.limit)}</p>` : ''}${layers ? `<div class="bf-badges">${layers}</div>` : ''}</div>`;
    }

    /* The assessment for the answers so far, or '' while there is nothing to assess. */
    assessment() {
      const s = this.strings;
      const state = this.state;
      const mode = state.mode;
      if (mode === 'new') {
        if (!this.answered()) return '';
        const features = [...state.features]
          .map((key) => this.feature(key))
          .filter(Boolean)
          .map((feature) => `<div class="bf-finding"><h4>${esc(feature.label)}</h4><p>${esc(feature.text)}</p></div>`)
          .join('');
        const useText = state.use ? fill(s.new_first_use, { use: state.use }) : '';
        return `<h3>${esc(s.result_title_new)}</h3>
          <div class="bf-finding"><h4>${esc(s.new_first_title)}</h4><p>${esc(fill(s.new_first_text, { use: useText }))}</p></div>${features}
          ${state.history === optionValue(s.history_options[0]) ? `<div class="bf-finding"><h4>${esc(s.history_problems_title)}</h4><p>${esc(s.history_problems_text)}</p></div>` : ''}`;
      }
      const picks = [...state.zones].map((key) => this.zone(key)).concat([...state.general].map((key) => this.general(key))).filter(Boolean);
      if (!picks.length) return '';
      let main = `<h3>${esc(mode === 'follow' ? s.result_title_follow : s.result_title_own)}</h3>${picks.map((item) => this.finding(item)).join('')}`;
      if (mode === 'own') {
        const shell = [...state.zones].some((key) => SHELL_ZONES.includes(key));
        main += `<div class="bf-finding bf-finding--plan"><h4>${esc(s.plan_title)}</h4><p>${esc(shell ? s.plan_shell : s.plan_other)}${state.model ? '' : ` ${esc(s.plan_model_hint)}`}</p></div>`;
        if (state.bought === optionValue(s.bought_options[1])) {
          main += `<div class="bf-finding"><h4>${esc(s.other_shop_title)}</h4><p>${esc(s.other_shop_text)}</p></div>`;
        }
      }
      return main;
    }

    /* The booking card that ends every appointment type: what it is, what it
       costs, the brief once there are answers, and the button into the widget. */
    bookingCard(brief, assessed) {
      const s = this.strings;
      const mode = this.state.mode;
      const service = this.data.services[mode];
      const meta = tidy(fill(s.meta, { minutes: service.minutes, price: service.price || s.price_included, days: service.days }));
      const text = brief ? s[`side_text_${mode}`] : s.direct_text;
      const briefBox = brief
        ? `<div class="bf-brief"><b>${esc(s.brief_label)}</b>${esc(brief)}<div class="bf-brief__actions"><button type="button" class="bf-brief__copy" data-bf-copy>${esc(s.copy)}</button><span>${esc(s.copy_hint)}</span></div></div>`
        : '';
      return `<div class="bf-result__side bf-book" data-bf-card>
        <div class="bf-book__copy"><p class="bf-book__label">${esc(s.next_label)}</p><h3>${esc(service.short)}</h3><p class="bf-book__meta">${esc(meta)}</p><p class="bf-book__text">${esc(text)}</p>${briefBox}</div>
        <div class="bf-book__act"><a class="button button-l button-green" href="${esc(this.data.bookingUrl)}" ${bookAttributes(mode)}>${esc(s.book)}</a><p class="bf-book__note">${esc(s.book_note)}</p></div>
        ${assessed ? `<p class="bf-result__fine">${esc(s.fine)}</p>` : ''}</div>`;
    }

    renderResult() {
      const s = this.strings;
      const state = this.state;
      const count = state.mode === 'new' ? 0 : state.zones.size + state.general.size;
      this.jump.hidden = !count;
      if (count) this.jump.querySelector('[data-bf-jump-label]').textContent = count === 1 ? s.jump_one : fill(s.jump_many, { count });
      const brief = this.answered() ? this.brief() : '';
      storeBrief(brief);
      storeBooking(this.bookingAnswers());
      const main = this.assessment();
      const card = this.bookingCard(brief, Boolean(main));
      this.result.innerHTML = main
        ? `<div class="bf-result"><div class="bf-result__main">${main}</div>${card}</div>`
        : `<div class="bf-result bf-result--card">${card}</div>`;
      if (this.bar) this.bar.watch(this.result.querySelector('[data-bf-card]'));
      this.renderBar();
    }

    renderBar() {
      if (!this.bar) return;
      const s = this.strings;
      const state = this.state;
      const service = this.data.services[state.chosen ? state.mode : 'own'];
      this.bar.render({
        mode: state.chosen ? state.mode : '',
        title: state.chosen ? service.short : s.bar_title,
        meta: tidy(fill(s.bar_meta, { minutes: service.minutes, price: service.price || s.price_included_short })),
      });
    }
  }

  /* ---------------------------------------------------------- booking bar */
  /* One booking button at the bottom of the screen on the long page. It shows
     once the hero has scrolled away and steps aside while another booking
     button, the check's booking card, the booking area or the footer is in
     view, and while a form field has focus (the phone keyboard is up). The
     body carries bf-bar-on and --bf-bar-h meanwhile, so the shop chat bubble
     can move up. */
  class BookingBar {
    constructor(check) {
      this.el = document.createElement('div');
      this.el.className = 'bf bf-bar';
      this.el.innerHTML = `<div class="bf-bar__inner"><p class="bf-bar__copy"><b data-bf-bar-title></b> <span data-bf-bar-meta></span></p><a class="button button-m button-green bf-bar__button" href="${esc(check.data.bookingUrl)}">${esc(check.strings.bar_book)}</a></div>`;
      this.el.inert = true;
      this.titleEl = this.el.querySelector('[data-bf-bar-title]');
      this.metaEl = this.el.querySelector('[data-bf-bar-meta]');
      this.link = this.el.querySelector('a');
      document.body.appendChild(this.el);
      this.inView = new Set();
      this.typing = false;
      this.ready = false;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? this.inView.add(entry.target) : this.inView.delete(entry.target)));
        this.ready = true;
        this.update();
      });
      [document.querySelector('.bf-hero'), ...document.querySelectorAll('a[data-bf-book]'), document.querySelector('.bf-bring'), document.querySelector('.site-footer')]
        .filter(Boolean)
        .forEach((target) => this.observer.observe(target));
      const isField = (node) => node instanceof Element && node.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select');
      document.addEventListener('focusin', (event) => {
        this.typing = isField(event.target);
        this.update();
      });
      document.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          this.typing = isField(document.activeElement);
          this.update();
        });
      });
      new ResizeObserver(() => document.body.style.setProperty('--bf-bar-h', `${this.el.offsetHeight}px`)).observe(this.el);
    }

    /* The check re-renders its booking card on every answer: keep watching the
       current one, and treat it as still in view until the observer reports. */
    watch(card) {
      const wasInView = this.card && this.inView.delete(this.card);
      if (this.card) this.observer.unobserve(this.card);
      this.card = card;
      if (!card) return;
      if (wasInView) this.inView.add(card);
      this.observer.observe(card);
    }

    render({ mode, title, meta }) {
      this.titleEl.textContent = title;
      this.metaEl.textContent = meta;
      const widget = pageWidget();
      this.link.dataset.bfBook = '';
      if (mode) this.link.dataset.bfBookMode = mode;
      else delete this.link.dataset.bfBookMode;
      if (widget) this.link.dataset.meetyWidget = widget;
    }

    update() {
      const on = this.ready && !this.inView.size && !this.typing;
      if (on === this.on) return;
      this.on = on;
      this.el.classList.toggle('is-on', on);
      this.el.inert = !on;
      document.body.classList.toggle('bf-bar-on', on);
    }
  }

  /* -------------------------------------------------------------- anatomy */
  class BootfittingAnatomy extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.stage = this.querySelector('[data-bf-stage]');
      this.panels = [...this.querySelectorAll('[data-bf-layer-panel]')];
      const row = this.querySelector('[data-bf-layers]');
      if (!row || !this.stage) return;
      this.tabs = wireTabs(row, (tab) => this.setLayer(tab.dataset.layer));
      const first = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      if (first) this.setLayer(first.dataset.layer);
    }

    setLayer(key) {
      this.stage.dataset.layer = key;
      selectTab(this.tabs, 'layer', key);
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.bfLayerPanel !== key;
      });
    }
  }

  /* ---------------------------------------------------------------- costs */
  class BootfittingCosts extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.panels = [...this.querySelectorAll('[data-bf-case-panel]')];
      this.addEventListener('change', (event) => {
        if (event.target.name === 'bf-case') this.show(event.target.value);
      });
      const checked = this.querySelector('input[name="bf-case"]:checked');
      if (checked) this.show(checked.value);
    }

    show(key) {
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.bfCasePanel !== key;
      });
    }
  }

  /* -------------------------------------------------------------- booking */
  class BootfittingBooking extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.brief = this.querySelector('[data-bf-booking-brief]');
      this.briefText = this.querySelector('[data-bf-booking-brief-text]');
      const update = (text) => {
        if (!this.brief) return;
        this.brief.hidden = !text;
        if (this.briefText) this.briefText.textContent = text || '';
      };
      let stored = '';
      try {
        stored = sessionStorage.getItem(BRIEF_KEY) || '';
      } catch (error) {
        /* ignore */
      }
      update(stored);
      document.addEventListener('bf:brief', (event) => update(event.detail.text));
      if (this.querySelector('.shopify-app-block')) watchMeety();
    }
  }

  /* ------------------------------------------------------- meety prefill */
  /* Meety's widget is a React app rendered into #popup-root on this page.
     Its controlled fields only take a value through the native setter plus an
     input event; radios through a click. Only empty fields are filled, each
     at most once, and only when the form belongs to the appointment type the
     customer chose in the check, so nothing typed or cleared is overwritten. */
  const prefilled = new WeakSet();
  let serviceOffered = false;
  let lastOffer = 0;
  /* Set by a booking button for one appointment type: until then the widget
     goes on from its service list to the calendar once that card is selected. */
  let advanceUntil = 0;

  const setNativeValue = (field, value) => {
    const proto = field.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, 'value').set.call(field, value);
    field.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const meetyQuestion = (modal, labels) => {
    const head = [...modal.querySelectorAll('.meety-input-label')].find((node) => labels.includes(node.textContent.replace(/\*/g, '').trim()));
    if (!head) return null;
    let group = head.parentElement;
    while (group && group !== modal && !group.querySelector('textarea, input')) group = group.parentElement;
    return group === modal ? null : group;
  };

  const fillText = (modal, labels, value) => {
    if (!value) return;
    const group = meetyQuestion(modal, labels);
    const field = group && group.querySelector('textarea, input:not([type="radio"]):not([type="checkbox"])');
    if (!field || field.value || prefilled.has(field)) return;
    prefilled.add(field);
    setNativeValue(field, value);
  };

  const fillRadio = (modal, question, value) => {
    if (!value) return;
    const names = question.options.find((pair) => pair[0] === value) || [value];
    const group = meetyQuestion(modal, question.label);
    if (!group || prefilled.has(group)) return;
    const options = [...group.querySelectorAll('.meety-radio-container')];
    if (options.some((option) => option.querySelector('input')?.checked)) return;
    const option = options.find((node) => names.includes(node.textContent.trim()));
    if (!option) return;
    prefilled.add(group);
    option.click();
  };

  const syncMeety = () => {
    const modal = document.querySelector('#popup-root .meety-modal-content');
    if (!modal) {
      serviceOffered = false;
      return;
    }
    const answers = readBooking();
    /* The default tab is no choice: then the customer picks the card. */
    const names = answers && answers.chosen && MEETY.services[answers.mode];
    if (!names) return;
    const namesService = (node) => {
      const text = node.textContent.toLowerCase();
      return names.some((name) => text.includes(name));
    };
    /* On /en/ Meety draws the cards again once the English titles arrive, and
       a click before that is lost; so offer the card until one is selected
       (by us or by the customer), then leave the choice alone. */
    const items = [...modal.querySelectorAll('.meety-service-item')];
    if (items.length && !serviceOffered) {
      const item = items.find(namesService);
      if (items.some((node) => /selected/.test(node.className))) {
        serviceOffered = true;
      } else if (item && Date.now() - lastOffer > 300) {
        lastOffer = Date.now();
        item.click();
        window.setTimeout(syncMeety, 350);
      }
    }
    if (items.length && advanceUntil > Date.now()) {
      const selected = items.find((node) => /selected/.test(node.className));
      const next = modal.querySelector('.meety-button.meety-button-primary:not(.meety-button-disabled)');
      if (selected && namesService(selected) && next) {
        advanceUntil = 0;
        next.click();
        return;
      }
    }
    if (!modal.querySelector('.meety-input-label') || !namesService(modal)) return;
    fillText(modal, MEETY.concern, answers.concern);
    if (answers.mode === 'own') {
      fillText(modal, MEETY.model, answers.model);
      fillRadio(modal, MEETY.bought, answers.bought);
    }
    if (answers.mode === 'new') fillRadio(modal, MEETY.use, answers.use);
  };

  let meetyWatched = false;
  const watchMeety = () => {
    if (meetyWatched) return;
    meetyWatched = true;
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        syncMeety();
      });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  if (!customElements.get('bootfitting-check')) customElements.define('bootfitting-check', BootfittingCheck);
  if (!customElements.get('bootfitting-anatomy')) customElements.define('bootfitting-anatomy', BootfittingAnatomy);
  if (!customElements.get('bootfitting-costs')) customElements.define('bootfitting-costs', BootfittingCosts);
  if (!customElements.get('bootfitting-booking')) customElements.define('bootfitting-booking', BootfittingBooking);

  /* Booking buttons in the page's Liquid (hero, visit) learn the widget here;
     the check card and the bar get it when they render. */
  const widget = pageWidget();
  if (widget) {
    document.querySelectorAll('a[data-bf-book]').forEach((link) => {
      link.dataset.meetyWidget = widget;
    });
  }

  /* A booking button for one appointment type makes that type the check's
     choice before meety-launch.js opens the widget. Without Meety the link
     just scrolls to the booking area. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-bf-book]');
    if (!link) return;
    const mode = link.dataset.bfBookMode;
    const check = document.querySelector('bootfitting-check');
    if (mode && check && check.choose) check.choose(mode);
    advanceUntil = mode && link.dataset.meetyWidget ? Date.now() + 8000 : 0;
  });

  /* Mode switches outside the check (situation cards, aftercare button). */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-bf-mode]');
    if (!trigger) return;
    const check = document.querySelector('bootfitting-check');
    if (!check || !check.setMode) return;
    event.preventDefault();
    check.setMode(trigger.dataset.bfMode, true);
  });

  const query = new URLSearchParams(location.search);
  const entry = ENTRY[query.get('modus')];
  /* Pass numbers as the schindele-bootfitting app issues them; anything else is ignored. */
  const passNo = (query.get('pass') || '').trim().toUpperCase();
  if (entry) {
    const apply = () => {
      const check = document.querySelector('bootfitting-check');
      if (check && check.setMode) {
        if (entry === 'follow' && /^SB-\d{4}-\d{4,6}$/.test(passNo) && check.state) check.state.passNo = passNo;
        check.setMode(entry, false);
        requestAnimationFrame(() => scrollTo(check));
      }
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply);
    else apply();
  }
})();
